import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '../config/stripe';
import './StripePaymentForm.css';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Card Element Options
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      fontFamily: 'system-ui, -apple-system, sans-serif',
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: true, // We'll collect this separately if needed
};

// Payment Form Component
const PaymentForm = ({ amount, customerEmail, customerName, onSubmit, onError, submitting }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setError(null);
    setProcessing(true);

    const cardElement = elements.getElement(CardElement);

    try {
      // Step 1: Create payment method from card element
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: customerName,
          email: customerEmail,
        },
      });

      if (pmError) {
        setError(pmError.message);
        onError(pmError.message);
        setProcessing(false);
        return;
      }

      // Step 2: Call onSubmit with payment method ID
      await onSubmit(paymentMethod.id);
    } catch (err) {
      const errorMessage = err.message || 'An error occurred while processing your payment.';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form">
      <div className="stripe-card-element-container">
        <label htmlFor="card-element">Card Details</label>
        <div className="stripe-card-element-wrapper">
          <CardElement
            id="card-element"
            options={cardElementOptions}
            onChange={(e) => {
              if (e.error) {
                setError(e.error.message);
              } else {
                setError(null);
              }
            }}
          />
        </div>
        {error && <div className="stripe-error-message">{error}</div>}
      </div>
      
      <button
        type="submit"
        className="stripe-submit-button"
        disabled={!stripe || processing || submitting}
      >
        {processing || submitting ? 'Processing...' : `Pay $${(amount / 100).toFixed(2)}`}
      </button>
    </form>
  );
};

// Main Stripe Payment Form Wrapper
const StripePaymentForm = ({ amount, customerEmail, customerName, onSubmit, onError, submitting }) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm
        amount={amount}
        customerEmail={customerEmail}
        customerName={customerName}
        onSubmit={onSubmit}
        onError={onError}
        submitting={submitting}
      />
    </Elements>
  );
};

export default StripePaymentForm;

